/**
 * features/call-recordings/hooks — hooks de leitura de gravações (STORY-06, TASK-34).
 *
 * useRecordingsBySubject: listagem paginada por subject via useTenantQuery (área "recordings").
 * useTranscript: transcrição diarizada de uma gravação.
 * useRecordingContext: trechos RAG de contexto via useTenantQuery (área "context").
 *
 * TODOS os hooks usam useTenantQuery para prefixar a queryKey com tenantId +
 * businessUnitId, garantindo isolamento por tenant (DD-004, RNF 2.3).
 * Não usar useQuery cru aqui.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  RecordingPage,
  Transcript,
  ContextChunk,
  SpeakerMapping,
  UpdateRecordingMetaInput,
  GlossaryItem,
  ReplaceTermInput,
  RecordingGlossary,
  PendingRecordingsPage,
  ClassifySubjectType,
  DesktopClientPlatform,
  DesktopClientRelease,
} from "./types.js";
import {
  listRecordingsBySubject,
  getTranscript,
  getContext,
  updateRecordingMeta,
  renameSpeakers,
  updateSegmentText,
  markRevised,
  replaceTerm,
  listGlossary,
  addGlossary,
  removeGlossary,
  generateSummary,
  updateSummary,
  getRecordingGlossary,
  updateGlossaryEntry,
  listPendingRecordings,
  classifyRecording,
  deletePendingRecording,
  getDesktopClientRelease,
} from "../../data/repos/recordingsRepo.js";
import type {
  ListRecordingsBySubjectParams,
  GetContextParams,
  ClassifyRecordingInput,
} from "../../data/repos/recordingsRepo.js";
import { useTenantQuery, useTenantScope } from "../../app/providers/QueryProvider.js";
import { buildQueryKey } from "../../data/queryKeys.js";

// ---------------------------------------------------------------------------
// useRecordingsBySubject
// ---------------------------------------------------------------------------

export interface UseRecordingsBySubjectResult {
  readonly recordings: RecordingPage | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => void;
}

/**
 * Carrega a lista paginada de gravações vinculadas a um subject.
 * `enabled` é falso quando `subjectId` está ausente.
 */
export function useRecordingsBySubject(
  params: ListRecordingsBySubjectParams,
): UseRecordingsBySubjectResult {
  const enabled = !!params.subjectId && !!params.subjectType;
  const query = useTenantQuery(
    "recordings:by-subject",
    () => listRecordingsBySubject(params),
    { params, staleTime: 30_000, enabled },
  );

  return {
    recordings: query.data ?? null,
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-012" : null,
    refetch: () => { void query.refetch(); },
  };
}

// ---------------------------------------------------------------------------
// useTranscript
// ---------------------------------------------------------------------------

export interface UseTranscriptResult {
  readonly transcript: Transcript | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => void;
}

/** Intervalo de polling do resumo enquanto em geração (ms). */
const SUMMARY_POLL_INTERVAL_MS = 4_000;

/**
 * Carrega a transcrição diarizada de uma gravação específica.
 * `enabled` é falso quando `recordingId` está ausente.
 *
 * Polling automático: quando `summaryStatus` é `pending` ou `generating`,
 * refaz o fetch a cada 4 s até o status sair desse estado.
 */
export function useTranscript(
  recordingId: string | undefined,
): UseTranscriptResult {
  const query = useTenantQuery(
    "recordings:transcript",
    () => {
      if (!recordingId) throw new Error("recordingId ausente");
      return getTranscript(recordingId);
    },
    {
      params: recordingId,
      staleTime: 60_000,
      enabled: !!recordingId,
      refetchInterval: (q) => {
        const s = q.state.data?.summaryStatus;
        return s === "pending" || s === "generating" ? SUMMARY_POLL_INTERVAL_MS : false;
      },
    },
  );

  return {
    transcript: query.data ?? null,
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-012" : null,
    refetch: () => { void query.refetch(); },
  };
}

// ---------------------------------------------------------------------------
// useRecordingContext
// ---------------------------------------------------------------------------

export interface UseRecordingContextResult {
  readonly chunks: readonly ContextChunk[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => void;
}

/**
 * Carrega trechos de contexto RAG de um subject. A busca por `q` é obrigatória:
 * o endpoint `/v1/context` exige `q` (min_length=1) e retorna 422 sem ele. Por isso
 * `enabled` só é verdadeiro quando há subject E um termo de busca não-vazio — evita
 * a requisição inicial sem `q` que resultava em 422 ao abrir a aba de gravações.
 */
export function useRecordingContext(
  params: GetContextParams,
): UseRecordingContextResult {
  const enabled =
    !!params.subjectId && !!params.subjectType && !!params.q?.trim();
  const query = useTenantQuery(
    "recordings:context",
    () => getContext(params),
    { params, staleTime: 60_000, enabled },
  );

  return {
    chunks: query.data ?? [],
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-012" : null,
    refetch: () => { void query.refetch(); },
  };
}

// ---------------------------------------------------------------------------
// Hooks de mutação — tela de revisão de gravação
// ---------------------------------------------------------------------------

/**
 * Retorna objeto com chave de invalidação do transcript para um recording.
 * Chamado internamente pelos mutation hooks.
 */
function useTranscriptQueryKey(recordingId: string) {
  const scope = useTenantScope();
  return buildQueryKey(
    "recordings:transcript",
    scope.tenantId,
    scope.businessUnitId,
    recordingId,
  );
}

/**
 * Chave de invalidação do glossário efetivo de uma gravação (aba Glossário).
 * Deve espelhar a key usada em {@link useRecordingGlossary}.
 */
function useRecordingGlossaryQueryKey(recordingId: string) {
  const scope = useTenantScope();
  return buildQueryKey(
    "recordings:recording-glossary",
    scope.tenantId,
    scope.businessUnitId,
    recordingId,
  );
}

// ---------------------------------------------------------------------------
// useUpdateRecordingMeta
// ---------------------------------------------------------------------------

export interface UseUpdateRecordingMetaResult {
  readonly save: (input: UpdateRecordingMetaInput) => Promise<void>;
  readonly isPending: boolean;
  readonly error: string | null;
}

/**
 * Atualiza metadados editáveis de uma gravação (título, local, participantes).
 * Invalida a query do transcript após salvar.
 */
export function useUpdateRecordingMeta(recordingId: string): UseUpdateRecordingMetaResult {
  const qc = useQueryClient();
  const transcriptKey = useTranscriptQueryKey(recordingId);

  const mutation = useMutation<void, Error, UpdateRecordingMetaInput>({
    mutationFn: (input) => updateRecordingMeta(recordingId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: transcriptKey });
    },
  });

  return {
    save: (input) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
    error: mutation.isError ? "WEB-ERR-013" : null,
  };
}

// ---------------------------------------------------------------------------
// useRenameSpeakers
// ---------------------------------------------------------------------------

export interface UseRenameSpeakersResult {
  readonly save: (mappings: readonly SpeakerMapping[]) => Promise<void>;
  readonly isPending: boolean;
  readonly error: string | null;
}

/**
 * Atualiza os nomes de exibição dos speakers de uma gravação.
 * Invalida a query do transcript após salvar.
 */
export function useRenameSpeakers(recordingId: string): UseRenameSpeakersResult {
  const qc = useQueryClient();
  const transcriptKey = useTranscriptQueryKey(recordingId);

  const mutation = useMutation<void, Error, readonly SpeakerMapping[]>({
    mutationFn: (mappings) => renameSpeakers(recordingId, mappings),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: transcriptKey });
    },
  });

  return {
    save: (mappings) => mutation.mutateAsync(mappings),
    isPending: mutation.isPending,
    error: mutation.isError ? "WEB-ERR-013" : null,
  };
}

// ---------------------------------------------------------------------------
// useUpdateSegmentText
// ---------------------------------------------------------------------------

export interface UseUpdateSegmentTextResult {
  readonly save: (sequence: number, text: string) => Promise<void>;
  readonly isPending: boolean;
  readonly error: string | null;
}

/**
 * Atualiza o texto de um segmento da transcrição.
 * Invalida a query do transcript após salvar.
 */
export function useUpdateSegmentText(recordingId: string): UseUpdateSegmentTextResult {
  const qc = useQueryClient();
  const transcriptKey = useTranscriptQueryKey(recordingId);

  const mutation = useMutation<void, Error, { sequence: number; text: string }>({
    mutationFn: ({ sequence, text }) => updateSegmentText(recordingId, sequence, text),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: transcriptKey });
    },
  });

  return {
    save: (sequence, text) => mutation.mutateAsync({ sequence, text }),
    isPending: mutation.isPending,
    error: mutation.isError ? "WEB-ERR-013" : null,
  };
}

// ---------------------------------------------------------------------------
// useMarkRevised
// ---------------------------------------------------------------------------

export interface UseMarkRevisedResult {
  readonly save: () => Promise<void>;
  readonly isPending: boolean;
  readonly error: string | null;
}

/**
 * Marca uma gravação como revisada.
 * Real → `POST /v1/recordings/{id}/revised` (204); mock → no-op.
 *
 * onSuccess invalida:
 * - recordings:transcript (chip e badge na tela de revisão atualizam)
 * - recordings:by-subject (badge "Transcrito→Revisado" na listagem atualiza)
 */
export function useMarkRevised(recordingId: string): UseMarkRevisedResult {
  const qc = useQueryClient();
  const transcriptKey = useTranscriptQueryKey(recordingId);
  const scope = useTenantScope();
  const bySubjectKey = buildQueryKey(
    "recordings:by-subject",
    scope.tenantId,
    scope.businessUnitId,
  );

  const mutation = useMutation<void, Error, void>({
    mutationFn: () => {
      if (!recordingId) return Promise.resolve();
      return markRevised(recordingId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: transcriptKey });
      void qc.invalidateQueries({ queryKey: bySubjectKey });
    },
  });

  return {
    save: () => mutation.mutateAsync(),
    isPending: mutation.isPending,
    error: mutation.isError ? "WEB-ERR-013" : null,
  };
}

// ---------------------------------------------------------------------------
// useGenerateSummary
// ---------------------------------------------------------------------------

export interface UseGenerateSummaryResult {
  readonly generate: () => Promise<void>;
  readonly isPending: boolean;
  readonly error: string | null;
}

/**
 * Solicita a geração assíncrona do resumo de uma gravação.
 * Real → `POST /v1/recordings/{id}/summary` (idempotente: 409 = já em geração).
 * Mock → no-op.
 *
 * onSuccess invalida a query do transcript para que o polling de summaryStatus
 * comece imediatamente (via refetchInterval em useTranscript).
 */
export function useGenerateSummary(recordingId: string): UseGenerateSummaryResult {
  const qc = useQueryClient();
  const transcriptKey = useTranscriptQueryKey(recordingId);

  const mutation = useMutation<void, Error, void>({
    mutationFn: () => generateSummary(recordingId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: transcriptKey });
    },
  });

  return {
    generate: () => mutation.mutateAsync(),
    isPending: mutation.isPending,
    error: mutation.isError ? "WEB-ERR-013" : null,
  };
}

// ---------------------------------------------------------------------------
// useUpdateSummary
// ---------------------------------------------------------------------------

export interface UseUpdateSummaryResult {
  readonly save: (text: string) => Promise<void>;
  readonly isPending: boolean;
  readonly error: string | null;
}

/**
 * Edita manualmente o texto do resumo de uma gravação.
 * Real → `PATCH /v1/recordings/{id}/summary` (204); mock → no-op.
 * onSuccess invalida a query do transcript para refletir o novo texto.
 */
export function useUpdateSummary(recordingId: string): UseUpdateSummaryResult {
  const qc = useQueryClient();
  const transcriptKey = useTranscriptQueryKey(recordingId);

  const mutation = useMutation<void, Error, string>({
    mutationFn: (text) => updateSummary(recordingId, text),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: transcriptKey });
    },
  });

  return {
    save: (text) => mutation.mutateAsync(text),
    isPending: mutation.isPending,
    error: mutation.isError ? "WEB-ERR-013" : null,
  };
}

// ---------------------------------------------------------------------------
// useReplaceTerm
// ---------------------------------------------------------------------------

export interface UseReplaceTermResult {
  readonly replace: (input: ReplaceTermInput) => Promise<void>;
  readonly isPending: boolean;
  readonly error: string | null;
}

/**
 * Substitui um termo na transcrição de uma gravação.
 * Invalida a query do transcript após sucesso.
 */
export function useReplaceTerm(recordingId: string): UseReplaceTermResult {
  const qc = useQueryClient();
  const transcriptKey = useTranscriptQueryKey(recordingId);
  const glossaryKey = useRecordingGlossaryQueryKey(recordingId);

  const mutation = useMutation<void, Error, ReplaceTermInput>({
    mutationFn: (input) => replaceTerm(recordingId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: transcriptKey });
      // "Adicionar ao glossário" cria/atualiza um termo → a aba Glossário reflete
      // imediatamente, sem refresh manual da página.
      void qc.invalidateQueries({ queryKey: glossaryKey });
    },
  });

  return {
    replace: (input) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
    error: mutation.isError ? "WEB-ERR-013" : null,
  };
}

// ---------------------------------------------------------------------------
// useRecordingGlossary
// ---------------------------------------------------------------------------

export interface UseRecordingGlossaryResult {
  readonly glossary: RecordingGlossary | null;
  readonly loading: boolean;
  readonly error: string | null;
}

/**
 * Carrega o glossário híbrido (global + conta) de uma gravação específica.
 * Real → `GET /v1/recordings/{id}/glossary`; mock → termos fictícios.
 * `enabled` é falso quando `recordingId` está ausente.
 */
export function useRecordingGlossary(recordingId: string): UseRecordingGlossaryResult {
  const query = useTenantQuery(
    "recordings:recording-glossary",
    () => getRecordingGlossary(recordingId),
    { params: recordingId, staleTime: 60_000, enabled: !!recordingId },
  );

  return {
    glossary: query.data ?? null,
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-012" : null,
  };
}

// ---------------------------------------------------------------------------
// useUpdateRecordingGlossaryEntry — edição inline na aba Glossário
// ---------------------------------------------------------------------------

export interface UpdateGlossaryEntryInput {
  readonly id: string;
  readonly term: string;
  readonly replacement: string;
}

export interface UseUpdateRecordingGlossaryEntryResult {
  readonly save: (input: UpdateGlossaryEntryInput) => Promise<void>;
  readonly isPending: boolean;
  readonly error: string | null;
}

/**
 * Edita uma entrada do glossário (termo/substituição) pela aba Glossário e
 * invalida a query do glossário da gravação — a lista reflete a alteração na hora.
 */
export function useUpdateRecordingGlossaryEntry(
  recordingId: string,
): UseUpdateRecordingGlossaryEntryResult {
  const qc = useQueryClient();
  const glossaryKey = useRecordingGlossaryQueryKey(recordingId);

  const mutation = useMutation<void, Error, UpdateGlossaryEntryInput>({
    mutationFn: async (input) => {
      await updateGlossaryEntry(input);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: glossaryKey });
    },
  });

  return {
    save: (input) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
    error: mutation.isError ? "WEB-ERR-014" : null,
  };
}

// ---------------------------------------------------------------------------
// usePendingRecordings — inbox de gravações aguardando classificação (Parte B)
// ---------------------------------------------------------------------------

export interface UsePendingRecordingsResult {
  readonly recordings: PendingRecordingsPage | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => void;
}

/** Intervalo de polling para detectar novas gravações sincronizadas pelo widget (ms). */
const PENDING_POLL_INTERVAL_MS = 30_000;

/**
 * Carrega a página de gravações aguardando classificação do usuário logado.
 * Realiza polling a cada 30 s para refletir novos syncs do widget desktop.
 * Erro mapeado para `WEB-ERR-015`.
 */
export function usePendingRecordings(): UsePendingRecordingsResult {
  const query = useTenantQuery<PendingRecordingsPage>(
    "recordings:pending",
    () => listPendingRecordings({ page: 1, pageSize: 50 }),
    { staleTime: 15_000, refetchInterval: PENDING_POLL_INTERVAL_MS },
  );

  return {
    recordings: query.data ?? null,
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-015" : null,
    refetch: () => { void query.refetch(); },
  };
}

// ---------------------------------------------------------------------------
// useClassifyRecording — classifica gravação vinculando a subject (Parte B)
// ---------------------------------------------------------------------------

export interface UseClassifyRecordingResult {
  readonly classify: (input: ClassifyRecordingInput) => Promise<void>;
  readonly isPending: boolean;
  readonly error: string | null;
}

/**
 * Classifica uma gravação vinculando-a a um subject (oportunidade ou parceiro).
 * Real → `POST /v1/recordings/{id}/classify`.
 *
 * onSuccess invalida:
 * - `recordings:pending` (gravação desaparece da inbox)
 * - `recordings:by-subject` (gravação aparece na listagem do subject)
 *
 * Erro mapeado para `WEB-ERR-015`.
 */
export function useClassifyRecording(): UseClassifyRecordingResult {
  const qc = useQueryClient();
  const scope = useTenantScope();

  const pendingKey = buildQueryKey("recordings:pending", scope.tenantId, scope.businessUnitId);
  const bySubjectKey = buildQueryKey("recordings:by-subject", scope.tenantId, scope.businessUnitId);

  const mutation = useMutation<void, Error, ClassifyRecordingInput>({
    mutationFn: (input) => classifyRecording(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: pendingKey });
      void qc.invalidateQueries({ queryKey: bySubjectKey });
    },
  });

  return {
    classify: (input) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
    error: mutation.isError ? "WEB-ERR-015" : null,
  };
}

// ---------------------------------------------------------------------------
// useDeletePendingRecording — apaga gravação pendente da inbox
// ---------------------------------------------------------------------------

export interface UseDeletePendingRecordingResult {
  readonly deleteRecording: (id: string) => Promise<void>;
  readonly isPending: boolean;
  readonly error: string | null;
}

/**
 * Apaga uma gravação aguardando classificação.
 * Real → `DELETE /v1/recordings/{id}`.
 *
 * onSuccess invalida `recordings:pending` (gravação desaparece da inbox).
 * Erro mapeado para `WEB-ERR-016`.
 */
export function useDeletePendingRecording(): UseDeletePendingRecordingResult {
  const qc = useQueryClient();
  const scope = useTenantScope();

  const pendingKey = buildQueryKey("recordings:pending", scope.tenantId, scope.businessUnitId);

  const mutation = useMutation<void, Error, string>({
    mutationFn: (id) => deletePendingRecording(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: pendingKey });
    },
  });

  return {
    deleteRecording: (id) => mutation.mutateAsync(id),
    isPending: mutation.isPending,
    error: mutation.isError ? "WEB-ERR-016" : null,
  };
}

// ---------------------------------------------------------------------------
// useDesktopClientRelease — distribuição do Azim Recorder (cliente desktop)
// ---------------------------------------------------------------------------

export interface UseDesktopClientReleaseResult {
  /**
   * `null` cobre dois casos distintos, discriminados por `loading`/`error`:
   * ainda carregando, ou carregamento concluído sem release publicada para a
   * plataforma (404 do backend — estado vazio válido, não um erro).
   */
  readonly release: DesktopClientRelease | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => void;
}

/**
 * Carrega a última release publicada do Azim Recorder para uma plataforma.
 * Real → `GET /v1/recordings/desktop-client/latest?platform=`.
 *
 * `release === null` com `loading === false` e `error === null` significa
 * "nenhuma release publicada para esta plataforma ainda" — a UI deve mostrar
 * um estado vazio honesto, nunca um botão de download sem destino.
 */
export function useDesktopClientRelease(
  platform: DesktopClientPlatform,
): UseDesktopClientReleaseResult {
  const query = useTenantQuery<DesktopClientRelease | null>(
    "recordings:desktop-client",
    () => getDesktopClientRelease({ platform }),
    {
      params: platform,
      staleTime: 30_000,
      // A downloadUrl é assinada e expira (DesktopClient__SignedUrlExpiry, 15 min por padrão),
      // mas o cartão a mantém num <a href> enquanto estiver montado. Como o portal desliga
      // refetchOnWindowFocus globalmente, uma aba deixada aberta guardaria uma URL morta e o
      // clique cairia num XML de 403 do GCS numa aba nova — que para o cliente é simplesmente
      // "o download está quebrado". Revalidamos um minuto antes do expiresAt que o próprio
      // backend informou, em vez de fixar um intervalo aqui: o prazo é decisão de configuração
      // do servidor e pode mudar sem que este arquivo saiba.
      refetchInterval: (query) => {
        const expiresAt = query.state.data?.expiresAt;
        if (!expiresAt) return false;
        const msLeft = new Date(expiresAt).getTime() - Date.now() - 60_000;
        return Number.isFinite(msLeft) ? Math.max(msLeft, 30_000) : false;
      },
    },
  );

  return {
    release: query.data ?? null,
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-017" : null,
    refetch: () => { void query.refetch(); },
  };
}

// ---------------------------------------------------------------------------
// Re-export ClassifySubjectType para conveniência de componentes (Parte B)
// ---------------------------------------------------------------------------

export type { ClassifySubjectType };

// ---------------------------------------------------------------------------
// useGlossary
// ---------------------------------------------------------------------------

export interface UseGlossaryResult {
  readonly items: readonly GlossaryItem[];
  readonly loading: boolean;
  readonly addItem: (term: string, replacement: string) => Promise<GlossaryItem>;
  readonly removeItem: (id: string) => Promise<void>;
  readonly isPending: boolean;
  readonly error: string | null;
}

/**
 * Gerencia o glossário de substituição de termos.
 * Lista, adiciona e remove itens com invalidação automática.
 */
export function useGlossary(): UseGlossaryResult {
  const qc = useQueryClient();
  const scope = useTenantScope();
  const glossaryKey = buildQueryKey("recordings:glossary", scope.tenantId, scope.businessUnitId);

  const query = useQuery({
    queryKey: glossaryKey,
    queryFn: () => listGlossary(),
    staleTime: 60_000,
  });

  const addMutation = useMutation<GlossaryItem, Error, { term: string; replacement: string }>({
    mutationFn: ({ term, replacement }) => addGlossary({ term, replacement }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: glossaryKey });
    },
  });

  const removeMutation = useMutation<void, Error, string>({
    mutationFn: (id) => removeGlossary(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: glossaryKey });
    },
  });

  const isPending = addMutation.isPending || removeMutation.isPending;
  const error =
    addMutation.isError || removeMutation.isError ? "WEB-ERR-013" : null;

  return {
    items: query.data ?? [],
    loading: query.isLoading,
    addItem: (term, replacement) => addMutation.mutateAsync({ term, replacement }),
    removeItem: (id) => removeMutation.mutateAsync(id),
    isPending,
    error,
  };
}
